import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRightLeft, ShieldCheck, Clock, CheckCircle2, XCircle, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { CARRIER_LINKS, ByonBanner } from "./Mobile";

const STATUS_META = {
    pending_payment: { label: "Awaiting payment", color: "text-amber-700 border-amber-300 bg-amber-50", icon: Clock },
    submitted: { label: "Submitted", color: "text-[#4C1D95] border-[#C4B5FD] bg-[#F5F0FF]", icon: Clock },
    in_review: { label: "In review", color: "text-amber-700 border-amber-300 bg-amber-50", icon: Loader2 },
    submitted_to_carrier: { label: "Sent to carrier", color: "text-[#4C1D95] border-[#C4B5FD] bg-[#F5F0FF]", icon: ArrowRightLeft },
    completed: { label: "Completed", color: "text-emerald-deep border-emerald-deep bg-[#ECFDF5]", icon: CheckCircle2 },
    rejected: { label: "Rejected", color: "text-red-600 border-red-300 bg-red-50", icon: XCircle },
    cancelled: { label: "Cancelled", color: "text-zinc-500 border-[#DDD6FE] bg-zinc-50", icon: XCircle },
};

const empty = {
    direction: "in",
    phone_number: "",
    account_holder_name: "",
    current_carrier: "",
    account_number: "",
    account_pin: "",
    billing_zip: "",
    service_address: "",
    authorize: false,
    notes: "",
};

export default function Porting() {
    const [form, setForm] = useState(empty);
    const [requests, setRequests] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/porting/my");
            setRequests(data);
        } catch {}
    };

    useEffect(() => { load(); }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.direction === "in" && !form.authorize) {
            toast.error("Please authorize the transfer to continue.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/porting/request", form);
            if (data.checkout_url) { window.location.href = data.checkout_url; return; }
            toast.success("Port request submitted — we'll be in touch by email & text.");
            setForm(empty);
            await load();
        } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Could not submit port request");
        } finally {
            setSubmitting(false);
        }
    };

    const cancel = async (id) => {
        if (!window.confirm("Cancel this port request?")) return;
        try {
            await api.post(`/porting/${id}/cancel`);
            toast.success("Request cancelled");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not cancel");
        }
    };

    return (
        <div className="space-y-8" data-testid="porting-page">
            <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">— Keep your number</p>
                <h1 className="font-display text-4xl font-bold tracking-tight">Bring your number to FlexLine Mobile</h1>
                <p className="text-zinc-600 mt-2 max-w-2xl">
                    Switching is easy — <span className="font-medium">keep the number you already have</span>. Our porting runs on
                    Telnyx, our wireless network partner, and your current service stays on until the transfer completes.
                    Most ports complete in <span className="font-medium">up to 24 hours</span>.
                </p>
            </div>

            <ByonBanner onCta={() => document.querySelector('[data-testid=porting-form]')?.scrollIntoView({ behavior: "smooth" })} />


            <div className="grid lg:grid-cols-5 gap-px bg-[#DDD6FE] border border-[#DDD6FE]">
                {/* Form */}
                <form onSubmit={submit} className="lg:col-span-3 bg-paper p-6 sm:p-8 space-y-5" data-testid="porting-form">
                    <div className="border border-[#DDD6FE] rounded-sm bg-[#FCFAFF] p-3 text-xs" data-testid="carrier-login-help">
                        <p className="font-medium text-zinc-700 mb-1.5">Don't have your account number or transfer PIN? Log in to your current carrier to find them:</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                            {CARRIER_LINKS.map((c) => (
                                <a key={c.name} href={c.url} target="_blank" rel="noopener noreferrer" className="text-emerald-deep underline" data-testid={`carrier-link-${c.name.toLowerCase().replace(/[^a-z]/g,'')}`}>{c.name} ↗</a>
                            ))}
                        </div>
                        <p className="text-zinc-500 mt-1.5">Tip: your transfer PIN is usually under Account → Number Transfer / Port Out. Keep your current service active until the transfer completes.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Direction">
                            <select
                                value={form.direction}
                                onChange={(e) => set("direction", e.target.value)}
                                data-testid="porting-direction"
                                className="w-full h-10 rounded-sm border border-[#DDD6FE] bg-white px-3 text-sm mt-1"
                            >
                                <option value="in">Port a number INTO Flex Line</option>
                                <option value="out">Port a number OUT of Flex Line</option>
                            </select>
                        </Field>
                        <Field label="Phone number to port">
                            <Input value={form.phone_number} onChange={(e) => set("phone_number", e.target.value)}
                                placeholder="(415) 555-0188" data-testid="porting-phone" className="rounded-sm font-mono mt-1" />
                        </Field>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Account holder name (as on the bill)">
                            <Input value={form.account_holder_name} onChange={(e) => set("account_holder_name", e.target.value)}
                                data-testid="porting-holder" className="rounded-sm mt-1" />
                        </Field>
                        <Field label="Current carrier (optional)">
                            <Input value={form.current_carrier} onChange={(e) => set("current_carrier", e.target.value)}
                                placeholder="Verizon, AT&T, T-Mobile…" data-testid="porting-carrier" className="rounded-sm mt-1" />
                        </Field>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Account number (with current carrier)">
                            <Input value={form.account_number} onChange={(e) => set("account_number", e.target.value)}
                                data-testid="porting-account" className="rounded-sm font-mono mt-1" />
                        </Field>
                        <Field label="Transfer PIN / passcode">
                            <Input value={form.account_pin} onChange={(e) => set("account_pin", e.target.value)}
                                data-testid="porting-pin" className="rounded-sm font-mono mt-1" />
                        </Field>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Billing ZIP code">
                            <Input value={form.billing_zip} onChange={(e) => set("billing_zip", e.target.value)}
                                data-testid="porting-zip" className="rounded-sm font-mono mt-1" />
                        </Field>
                        <Field label="Service address (optional)">
                            <Input value={form.service_address} onChange={(e) => set("service_address", e.target.value)}
                                data-testid="porting-address" className="rounded-sm mt-1" />
                        </Field>
                    </div>

                    {form.direction === "in" && (
                        <label className="flex items-start gap-3 border border-[#DDD6FE] rounded-sm p-4 bg-[#FCFAFF] cursor-pointer" data-testid="porting-authorize-label">
                            <input type="checkbox" checked={form.authorize} onChange={(e) => set("authorize", e.target.checked)}
                                data-testid="porting-authorize" className="mt-1" />
                            <span className="text-sm text-zinc-700">
                                <ShieldCheck className="w-4 h-4 inline mr-1 text-emerald-deep" />
                                I authorize Flex Line to transfer this number from my current carrier on my behalf (Letter of
                                Authorization). I confirm the details above are accurate.
                            </span>
                        </label>
                    )}

                    <Button type="submit" disabled={submitting} data-testid="porting-submit"
                        className="w-full bg-espresso text-[#FAF5FF] hover:bg-black rounded-sm h-11">
                        {submitting ? "Submitting…" : form.direction === "in" ? "Pay $10 & submit port request" : "Submit port request"}
                    </Button>
                    {form.direction === "in" && (
                        <p className="text-xs text-zinc-600" data-testid="porting-fee-note">
                            A one-time $10.00 transfer fee is charged before we start the transfer. A paid FlexLine Mobile line is required.
                        </p>
                    )}
                    <p className="text-xs text-zinc-500">
                        🔒 Your account number and PIN are encrypted. Do not cancel service with your current carrier until the port completes.
                    </p>
                </form>

                {/* How it works */}
                <div className="lg:col-span-2 bg-espresso text-[#FAF5FF] p-6 sm:p-8">
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[#C4B5FD] mb-4">— How porting works</p>
                    <ol className="space-y-5">
                        {[
                            ["Submit your details", "Tell us the number, carrier, account # and transfer PIN."],
                            ["We verify & file", "We validate the number and file the port with your carrier."],
                            ["Carrier processes", "Usually within 24 hours. Your current service keeps working."],
                            ["Number goes live", "It activates on Flex Line — texts & calls start flowing here."],
                        ].map(([t, d], i) => (
                            <li key={t} className="flex gap-3">
                                <span className="font-mono text-sm text-[#C4B5FD] mt-0.5">0{i + 1}</span>
                                <div>
                                    <div className="font-medium text-sm">{t}</div>
                                    <div className="text-sm text-zinc-300 mt-0.5">{d}</div>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>

            {/* My requests */}
            <div>
                <h2 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Your port requests</h2>
                {requests.length === 0 ? (
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-10 text-center text-sm text-zinc-500" data-testid="porting-empty">
                        <Phone className="w-6 h-6 mx-auto text-[#C4B5FD] mb-3" />
                        No port requests yet. Submit one above to bring your number over.
                    </div>
                ) : (
                    <div className="space-y-px bg-[#DDD6FE] border border-[#DDD6FE]">
                        {requests.map((r) => {
                            const meta = STATUS_META[r.status] || STATUS_META.submitted;
                            const Icon = meta.icon;
                            return (
                                <div key={r.request_id} className="bg-paper p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`porting-row-${r.request_id}`}>
                                    <div>
                                        <div className="font-mono text-lg font-medium">{r.phone_number}</div>
                                        <div className="text-xs text-zinc-500 mt-0.5">
                                            Port {r.direction === "in" ? "in" : "out"}
                                            {r.current_carrier ? ` · ${r.current_carrier}` : ""}
                                            {r.carrier_lookup?.line_type ? ` · ${r.carrier_lookup.line_type}` : ""}
                                            {" · "}{new Date(r.created_at).toLocaleDateString()}
                                        </div>
                                        {r.admin_note && <div className="text-xs text-zinc-600 mt-1 italic">“{r.admin_note}”</div>}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`font-mono text-[10px] uppercase px-2 py-1 rounded-sm border inline-flex items-center gap-1 ${meta.color}`} data-testid={`porting-status-${r.request_id}`}>
                                            <Icon className="w-3 h-3" /> {meta.label}
                                        </span>
                                        {["submitted", "in_review"].includes(r.status) && (
                                            <Button onClick={() => cancel(r.request_id)} variant="outline"
                                                data-testid={`porting-cancel-${r.request_id}`}
                                                className="rounded-sm h-8 px-3 text-xs border-[#DDD6FE]">
                                                Cancel
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="font-mono text-[10px] uppercase text-zinc-500">{label}</label>
            {children}
        </div>
    );
}
